import { lazy, Suspense } from "react";
import { ApiRequestError } from "../lib/api.ts";
import { useT } from "../lib/i18n.tsx";

/** The quiet loading state: a pulse in the signal colour, never a spinner over content. */
export function FullPageLoading() {
  const t = useT();
  return (
    <div className="grid min-h-dvh place-items-center" aria-busy="true">
      <div className="flex items-center gap-3 text-sm text-muted">
        <span className="size-2 animate-pulse rounded-full bg-signal" aria-hidden />
        {t("common.loading")}
      </div>
    </div>
  );
}

/** What went wrong, in the reader's words, and the one thing to do next. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const message =
    error instanceof ApiRequestError
      ? error.code === "offline"
        ? t("errors.offline")
        : error.message
      : t("screens.error.lead");
  return (
    <div className="grid min-h-[60dvh] place-items-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">{t("screens.error.title")}</h1>
        <p className="mt-2 text-muted">{message}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-6 inline-flex h-10 items-center rounded-pill bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-2"
          >
            {t("common.retry")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** A page that isn't there: loaded on demand, so the first load stays small (`not-found-view.tsx`). */
const LazyNotFound = lazy(() => import("./not-found-view.tsx"));

export function NotFoundView(props: { inShell?: boolean }) {
  return (
    <Suspense fallback={null}>
      <LazyNotFound {...props} />
    </Suspense>
  );
}
